'use client';

import { useState, useEffect, useRef } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { signIn } from 'next-auth/react';
import { useSessionContext } from '@/providers/session-provider';
import { collectDeviceFingerprint } from '@/lib/fingerprint';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2 } from 'lucide-react';

const SignInPage = () => {
  const { session } = useSessionContext();
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [rememberMe, setRememberMe] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [initialMessage, setInitialMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const router = useRouter();
  const deviceFingerprintRef = useRef<string | null>(null);

  // Collect device fingerprint on mount (for account sharing detection)
  useEffect(() => {
    collectDeviceFingerprint()
      .then((hash) => {
        deviceFingerprintRef.current = hash;
      })
      .catch(() => {
        // Fingerprint collection is optional - don't block login
        deviceFingerprintRef.current = null;
      });
  }, []);

  // Redirect already authenticated users
  useEffect(() => {
    if (session?.user?.id) {
      router.push('/dashboard');
    }
  }, [session, router]);

  // Don't render form if already authenticated (prevents flash)
  if (session?.user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-sidebar px-4 py-8">
        <Card className="w-full max-w-md shadow-lg border-muted/20">
          <CardContent className="flex items-center justify-center py-12">
            <div className="flex items-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="ml-2 text-muted-foreground">Redirecting...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsLoading(true);
    setError(null);
    setInitialMessage(null);

    try {
      const result = await signIn('credentials', {
        redirect: false,
        callbackUrl: '/dashboard',
        email: email,
        password: password,
        rememberMe: rememberMe,
        deviceFingerprint: deviceFingerprintRef.current || undefined,
      });

      if (result?.error) {
        if (result.error === 'Configuration') {
          setError('Too many login attempts. Please try again later.');
        } else if (result.error === 'CredentialsSignin') {
          try {
            await fetch('/api/auth/check-initial-password', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ email }),
            });
            setInitialMessage('If your account is new, check your email for setup instructions or contact support.');
            setError('Invalid email or password.');
          } catch (checkError) {
            console.error('Failed to check initial password status or request setup:', checkError);
            setError('Invalid email or password.');
          }
        } else {
          setError(result.error || 'An unexpected sign-in error occurred.');
        }
      } else if (result?.url) {
        window.location.href = result.url;
        return;
      } else if (result?.ok) {
        router.push('/dashboard');
      } else {
        setError('An unexpected sign-in issue occurred.');
      }
    } catch (err) {
      console.error('Sign-in process failed:', err);
      setError('An unexpected error occurred during sign-in.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-sidebar px-4 py-8">
      <Card className="w-full max-w-md shadow-lg border-muted/20">
        <CardHeader className="flex flex-col items-center space-y-2 pb-0">
          <Image
            src="/images/logo-small.png"
            alt="Mobius Poker Logo"
            width={120}
            height={120}
            className="mb-2 transition-transform duration-300 hover:scale-105"
            priority
          />
          <CardTitle className="text-2xl font-bold text-center">Sign In</CardTitle>
          <CardDescription className="text-center text-muted-foreground">
            Enter your credentials to access your account
          </CardDescription>
        </CardHeader>
        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-6">
            <div className="space-y-3">
              <Label htmlFor="email" className="text-sm font-medium">Email</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                placeholder="m@example.com"
                required
                value={email}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
                disabled={isLoading}
                className="h-10"
              />
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-sm font-medium">Password</Label>
                <Link
                  href="/forgot-password"
                  className="text-xs text-primary hover:underline transition-colors"
                  tabIndex={-1}
                >
                  Forgot your password?
                </Link>
              </div>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
                disabled={isLoading}
                className="h-10"
              />
            </div>
            <div className="flex items-center space-x-2 mt-1">
              <Checkbox
                id="remember-me"
                checked={rememberMe}
                onCheckedChange={(checked) => setRememberMe(!!checked)}
              />
              <Label 
                htmlFor="remember-me" 
                className="text-sm font-medium cursor-pointer"
              >
                Remember me
              </Label>
            </div>
            {error && (
              <div className="rounded-md bg-destructive/10 px-3 py-2">
                <p className="text-sm font-medium text-destructive">{error}</p>
              </div>
            )}
            {initialMessage && (
              <div className="rounded-md bg-green-100 dark:bg-green-900/20 px-3 py-2">
                <p className="text-sm font-medium text-green-600 dark:text-green-500">{initialMessage}</p>
              </div>
            )}
          </CardContent>
          <CardFooter className="flex flex-col space-y-4 px-6 pb-6 pt-2">
            <Button type="submit" className="w-full h-10" disabled={isLoading}>
              {isLoading ? 'Signing In...' : 'Sign In'}
            </Button>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
};

export default SignInPage; 
